import { Component, computed, ElementRef, HostListener, inject, Renderer2, signal, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { CanvasService } from '../../services/canvas.service';
import { ExportImportService } from '../../services/export-import.service';
import { SHAPE_TOOLS, TOOLS, ToolType } from '../../models/tool.model';

@Component({
    selector: 'app-tool-panel',
    imports: [CommonModule],
    templateUrl: './tool-panel.component.html',
    styleUrl: './tool-panel.component.scss'
})
export class ToolPanelComponent {
    @ViewChild('imageFileInput') imageFileInput!: ElementRef<HTMLInputElement>;
    @ViewChild('shapesButton') shapesButton!: ElementRef<HTMLButtonElement>;

    private renderer = inject(Renderer2);

    readonly canvasService = inject(CanvasService);
    private readonly exportImportService = inject(ExportImportService);
    readonly tools = TOOLS;
    readonly shapeTools = SHAPE_TOOLS;
    readonly currentTool = this.canvasService.currentTool;
    readonly gridVisible = this.canvasService.gridVisible;
    readonly snapToGrid = this.canvasService.snapToGrid;
    readonly zoom = this.canvasService.zoom;
    readonly canUndo = this.canvasService.canUndo;
    readonly canRedo = this.canvasService.canRedo;
    readonly selectedIds = this.canvasService.selectedIds;

    readonly showShapesDropdown = signal(false);
    shapesDropdownElement: HTMLElement | null = null;

    readonly zoomPercent = computed(() => Math.round(this.zoom() * 100));
    readonly hasSelection = computed(() => this.selectedIds().length > 0);
    readonly selectedCount = computed(() => this.selectedIds().length);

    selectTool(type: ToolType): void {
        this.canvasService.setCurrentTool(type);
    }

    @HostListener('document:click', ['$event'])
    closeDropdownOnClickOutside(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.tool-dropdown-wrapper')) {
            this.showShapesDropdown.set(false);
            if (this.shapesDropdownElement && this.shapesDropdownElement.parentNode) {
                this.renderer.removeChild(document.body, this.shapesDropdownElement);
                this.shapesDropdownElement = null;
            }
        }
    }

    toggleShapesDropdown(): void {
        const isOpen = this.showShapesDropdown();
        if (isOpen) {
            this.showShapesDropdown.set(false);
            if (this.shapesDropdownElement && this.shapesDropdownElement.parentNode) {
                this.renderer.removeChild(document.body, this.shapesDropdownElement);
                this.shapesDropdownElement = null;
            }
        } else {
            this.showShapesDropdown.set(true);
            this.createShapesDropdownPortal();
        }
    }

    private createShapesDropdownPortal(): void {
        if (!this.shapesButton) return;

        // Create dropdown element
        const dropdown = this.renderer.createElement('div');
        this.renderer.addClass(dropdown, 'shapes-dropdown-portal');

        // Get button position
        const buttonRect = this.shapesButton.nativeElement.getBoundingClientRect();
        const panelWidth = 240;

        // Position dropdown to the right of the tool panel, full width
        this.renderer.setStyle(dropdown, 'position', 'fixed');
        this.renderer.setStyle(dropdown, 'left', (buttonRect.left + panelWidth) + 'px');
        this.renderer.setStyle(dropdown, 'top', buttonRect.top + 'px');
        this.renderer.setStyle(dropdown, 'width', panelWidth + 'px');
        this.renderer.setStyle(dropdown, 'background', '#2c2c2c');
        this.renderer.setStyle(dropdown, 'border', '1px solid #404040');
        this.renderer.setStyle(dropdown, 'border-radius', '6px');
        this.renderer.setStyle(dropdown, 'z-index', '1000');
        this.renderer.setStyle(dropdown, 'box-shadow', '0 4px 12px rgba(0, 0, 0, 0.5)');
        this.renderer.setStyle(dropdown, 'max-height', '400px');
        this.renderer.setStyle(dropdown, 'overflow-y', 'auto');

        // Add shape options
        this.shapeTools.forEach((shape, index) => {
            const btn = this.renderer.createElement('button');
            this.renderer.addClass(btn, 'shape-option-portal');
            if (this.currentTool() === shape.type) {
                this.renderer.addClass(btn, 'active');
            }

            const iconSpan = this.renderer.createElement('span');
            this.renderer.addClass(iconSpan, 'tool-icon');
            this.renderer.setProperty(iconSpan, 'textContent', shape.icon);

            const nameSpan = this.renderer.createElement('span');
            this.renderer.addClass(nameSpan, 'tool-name');
            this.renderer.setProperty(nameSpan, 'textContent', shape.name);

            this.renderer.appendChild(btn, iconSpan);
            this.renderer.appendChild(btn, nameSpan);

            if (shape.shortcut) {
                const shortcutSpan = this.renderer.createElement('span');
                this.renderer.addClass(shortcutSpan, 'tool-shortcut');
                this.renderer.setProperty(shortcutSpan, 'textContent', shape.shortcut);
                this.renderer.appendChild(btn, shortcutSpan);
            }

            this.renderer.listen(btn, 'click', () => {
                this.selectShapeAndClose(shape.type);
            });

            this.renderer.appendChild(dropdown, btn);
        });

        // Add styles for portal-based dropdown
        const styleSheet = document.createElement('style');
        styleSheet.textContent = `
            .shapes-dropdown-portal {
                font-family: inherit;
            }
            .shape-option-portal {
                display: flex;
                align-items: center;
                gap: 8px;
                width: 100%;
                padding: 10px 12px;
                background: transparent;
                border: none;
                border-bottom: 1px solid #404040;
                color: #e0e0e0;
                cursor: pointer;
                transition: all 0.2s;
                font-size: 13px;
                text-align: left;
                font-family: inherit;
            }
            .shape-option-portal:last-child {
                border-bottom: none;
            }
            .shape-option-portal:hover {
                background: #404040;
            }
            .shape-option-portal.active {
                background: #0078d4;
                color: #ffffff;
            }
            .shape-option-portal .tool-icon {
                font-size: 18px;
                width: 24px;
                text-align: center;
            }
            .shape-option-portal .tool-name {
                flex: 1;
            }
            .shape-option-portal .tool-shortcut {
                font-size: 11px;
                opacity: 0.7;
                background: rgba(255, 255, 255, 0.1);
                padding: 2px 6px;
                border-radius: 3px;
            }
        `;
        if (!document.getElementById('shapes-dropdown-styles')) {
            styleSheet.id = 'shapes-dropdown-styles';
            document.head.appendChild(styleSheet);
        }

        this.renderer.appendChild(document.body, dropdown);
        this.shapesDropdownElement = dropdown;
    }

    selectShapeAndClose(type: ToolType): void {
        this.canvasService.setCurrentTool(type);
        this.showShapesDropdown.set(false);
    }

    isShapeTool(tool: ToolType): boolean {
        return SHAPE_TOOLS.some(shape => shape.type === tool);
    }

    getActiveShapeIcon(): string {
        const activeTool = this.currentTool();
        const shape = SHAPE_TOOLS.find(s => s.type === activeTool);
        return shape?.icon || '▭';
    }

    toggleGrid(): void {
        this.canvasService.setGridVisible(!this.gridVisible());
    }

    toggleSnapToGrid(): void {
        this.canvasService.setSnapToGrid(!this.snapToGrid());
    }

    zoomIn(): void {
        this.canvasService.setZoom(this.zoom() * 1.2);
    }

    zoomOut(): void {
        this.canvasService.setZoom(this.zoom() / 1.2);
    }

    resetZoom(): void {
        this.canvasService.setZoom(1);
        this.canvasService.setPan({ x: 0, y: 0 });
    }

    deleteSelected(): void {
        const ids = this.selectedIds();
        if (ids.length > 0) {
            this.canvasService.deleteObjects(ids);
        }
    }

    addImage(): void {
        this.imageFileInput.nativeElement.click();
    }

    async onImageFileSelected(event: Event): Promise<void> {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        try {
            // Check if file is SVG and use importSVG instead
            if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
                await this.exportImportService.importSVG(file);
            } else {
                await this.exportImportService.importImage(file);
            }
        } catch (error) {
            console.error('Failed to add image:', error);
            alert('Failed to add image');
        } finally {
            // Reset file input
            input.value = '';
        }
    }
}
